import { ArticleType } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { NoPaymentMethodsAvailableDialog } from '@/components/modules/Application/components/_shared/components/NoPaymentMethodsAvailableDialog';
import { AgentSelection } from '@/components/modules/Application/components/AgentSelection/AgentSelection';
import { AutoCart } from '@/components/modules/Application/components/Cart/AutoCart/AutoCart';
import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { DefaultCart } from '@/components/modules/Application/components/Cart/DefaultCart/DefaultCart';
import { KtuinCart } from '@/components/modules/Application/components/Cart/KtuinCart/KtuinCart';
import { CheckoutSessionCreation } from '@/components/modules/Application/components/CheckoutSessionCreation/CheckoutSessionCreation';
import { ContactInformation } from '@/components/modules/Application/components/ContactInformation/ContactInformation';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { PaymentMethod } from '@/components/modules/Application/components/PaymentMethod/PaymentMethod';
import { References } from '@/components/modules/Application/components/References/References';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { useGetApplicationSteps } from '@/components/modules/Application/hooks/useGetApplicationSteps';
import { ApplicationStep } from '@/components/modules/Application/types/ApplicationStep';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const ApplicationSteps = () => {
  const {
    state: { cart, selectedAgent, checkoutSessionId, showSummary },
    actions: { updateApplicationContext, setShowSummary },
  } = useApplicationContext();

  const { initApplication, resetApplicationContext, step, resetNavigation } = useApplicationNavigation();
  const { getSteps } = useGetApplicationSteps();
  const { updateAgentPaymentConfiguration, selectedAgentHasError } = usePaymentMethods();

  useEffect(() => {
    initApplication();
    resetNavigation(getSteps());
    return () => {
      setTimeout(() => resetApplicationContext(), 100);
    };
  }, []);

  useEffect(() => {
    const articleType = cart?.cartArticleType();

    if (!articleType || articleType !== ArticleType.AUTOMOBILE) {
      return;
    }

    if (step === ApplicationStep.PAYMENT_METHOD) {
      setShowSummary(false);
    } else {
      if (showSummary === false) {
        setShowSummary(true);
      }
    }
  }, [step]);

  useEffect(() => {
    if (selectedAgent) {
      updateApplicationContext({ checkoutSessionId: uuidv4() });
      updateAgentPaymentConfiguration(selectedAgent.id);
      updateApplicationContext({ cart: CartEntity.initial(selectedAgent.allowedPurchaseCountries[0]) });
      resetNavigation(getSteps());
    }
  }, [selectedAgent]);

  if (step === ApplicationStep.AGENT_SELECTION) {
    return <AgentSelection />;
  }

  if (selectedAgentHasError === undefined || !checkoutSessionId) {
    return <CustomPaper loading={true} />;
  }

  if (selectedAgentHasError) {
    return <NoPaymentMethodsAvailableDialog />;
  }

  switch (step) {
    case ApplicationStep.REFERENCES:
      return <References />;
    case ApplicationStep.DEFAULT_CART:
      return <DefaultCart />;
    case ApplicationStep.AUTO_CART:
      return <AutoCart />;
    case ApplicationStep.KTUIN_CART:
      return <KtuinCart />;
    case ApplicationStep.PAYMENT_METHOD:
      return <PaymentMethod />;
    case ApplicationStep.CONTACT_INFORMATION:
      return <ContactInformation />;
    case ApplicationStep.CHECKOUT_SESSION_CREATION:
      return <CheckoutSessionCreation />;
    default:
      return <CustomPaper loading={true} />;
  }
};
